/**
 * A React component for wavesurfer.js
 *
 * Usage:
 *
 * import WavesurferPlayer from '@wavesurfer/react'
 *
 * <WavesurferPlayer
 *   url="/my-server/audio.ogg"
 *   waveColor="purple"
 *   height={100}
 *   onReady={(wavesurfer) => console.log('Ready!', wavesurfer)}
 * />
 */

import { useEffect, useMemo, useState, type RefObject } from "react"

import WaveSurfer, {
  type WaveSurferEvents,
  type WaveSurferOptions,
} from "wavesurfer.js"

type WavesurferEventHandler<T extends unknown[]> = (
  wavesurfer: WaveSurfer,
  ...args: T
) => void

type OnWavesurferEvents = {
  [K in keyof WaveSurferEvents as `on${Capitalize<K>}`]?: WavesurferEventHandler<
    WaveSurferEvents[K]
  >
}

type PartialWavesurferOptions = Omit<WaveSurferOptions, "container">

/**
 * Props for the Wavesurfer component
 * @public
 */
export type WavesurferProps = PartialWavesurferOptions & OnWavesurferEvents

const wavesurferPeaks = new Map<string, Array<Float32Array | number[]>>()
/**
 * Use wavesurfer instance
 */
function useWavesurferInstance(
  containerRef: RefObject<HTMLDivElement | null>,
  options: Partial<WaveSurferOptions>,
): WaveSurfer | null {
  const [wavesurfer, setWavesurfer] = useState<WaveSurfer | null>(null)

  // Track options by URL only - this is the main thing that should trigger recreation
  // Other options like waveColor, height etc. don't need to recreate the instance
  const url = options.url

  // Create a wavesurfer instance - only recreate when URL changes
  useEffect(() => {
    if (!containerRef?.current || !url) {
      return
    }

    const ws = WaveSurfer.create({
      ...options,
      container: containerRef.current,
      peaks: url ? wavesurferPeaks.get(url) : undefined,
    })

    setWavesurfer(ws)

    return () => {
      if (ws) {
        // Stop any ongoing playback
        ws.pause()
        // Unsubscribe from all events
        ws.unAll()
        // Destroy the instance
        ws.destroy()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- legacy dependency suppression pending refactor
  }, [containerRef, url])

  return wavesurfer
}

/**
 * Use wavesurfer state
 */
function useWavesurferState(wavesurfer: WaveSurfer | null): {
  isReady: boolean
  isPlaying: boolean
  currentTime: number
} {
  const [isReady, setIsReady] = useState<boolean>(false)
  const [isPlaying, setIsPlaying] = useState<boolean>(false)
  const [hasFinished, setHasFinished] = useState<boolean>(false)
  const [currentTime, setCurrentTime] = useState<number>(0)

  useEffect(() => {
    if (!wavesurfer) {
      return
    }

    const unsubscribeFns = [
      wavesurfer.on("load", () => {
        setIsReady(false)
        setIsPlaying(false)
        setCurrentTime(0)
      }),

      wavesurfer.on("ready", () => {
        setIsReady(true)
        setIsPlaying(false)
        setHasFinished(false)
        setCurrentTime(0)
        if (wavesurfer.options.url) {
          wavesurferPeaks.set(wavesurfer.options.url, wavesurfer.exportPeaks())
        }
      }),

      wavesurfer.on("finish", () => {
        setHasFinished(true)
      }),

      wavesurfer.on("play", () => {
        setIsPlaying(true)
      }),

      wavesurfer.on("pause", () => {
        setIsPlaying(false)
      }),

      wavesurfer.on("timeupdate", () => {
        setCurrentTime(wavesurfer.getCurrentTime())
      }),

      wavesurfer.on("destroy", () => {
        setIsReady(false)
        setIsPlaying(false)
      }),
    ]

    return () => {
      unsubscribeFns.forEach((fn) => fn())
    }
  }, [wavesurfer])

  return useMemo(
    () => ({
      isReady,
      isPlaying,
      hasFinished,
      currentTime,
    }),
    [isPlaying, hasFinished, currentTime, isReady],
  )
}

export function useWavesurfer({
  container,
  ...options
}: Omit<WaveSurferOptions, "container"> & {
  container: RefObject<HTMLDivElement | null>
}): ReturnType<typeof useWavesurferState> & {
  wavesurfer: ReturnType<typeof useWavesurferInstance>
} {
  const wavesurfer = useWavesurferInstance(container, options)
  const state = useWavesurferState(wavesurfer)
  return useMemo(() => ({ ...state, wavesurfer }), [state, wavesurfer])
}
